import { Kiosk } from "domains/kiosk/aggregates/Kiosk";
import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import { useHistory } from "react-router-dom";
import styles from "./IdleTime.module.scss";

const DELAY = 1000;
const NO_TIMER = 9999;
const EXPIRATION_DEFAULT_TIME = 30;

function useInterval(callback: () => void, delay: number) {
  const savedCallback = useRef<() => void>();

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    let id = setInterval(() => {
      savedCallback.current && savedCallback.current();
    }, delay);
    return () => clearInterval(id);
  }, [delay]);
}

interface IdleContext {
  resetCounter: () => void;
  handleActiveIdleTimer: () => void;
  handleActiveDefaultIdleTimer: () => void;
  handleDisableIdleTimer: () => void;
}

const IdleContext = createContext<IdleContext>({} as IdleContext);

interface IdleContextProviderProps {
  kiosk: Kiosk | null;
  children: ReactNode;
}

interface IdleCounterDisplayProps {
  counterRef: React.MutableRefObject<number>;
  expirationTime: React.MutableRefObject<number>;
  isActiveRef: React.MutableRefObject<boolean>;
  onExpire: () => void;
}

const IdleCounterDisplay: FC<IdleCounterDisplayProps> = ({
  counterRef,
  expirationTime,
  isActiveRef,
  onExpire,
}) => {
  const [renderCounter, setRenderCounter] = useState<number | "">("");

  useInterval(() => {
    if (!isActiveRef.current) return;
    counterRef.current -= 1;
    console.log("[IdleTimer] counter:", counterRef.current);
    if (counterRef.current <= 10) {
      setRenderCounter(counterRef.current);
    }
    if (counterRef.current <= 0) {
      console.log("[IdleTimer] expired — redirecting to /");
      setRenderCounter("");
      onExpire();
    }
  }, DELAY);

  useEffect(() => {
    const handleClick = () => {
      if (!isActiveRef.current) return;
      counterRef.current = expirationTime.current;
      console.log("[IdleTimer] click reset — counter back to:", counterRef.current);
      setRenderCounter("");
    };
    window.addEventListener("click", handleClick);
    return () => window.removeEventListener("click", handleClick);
  }, [counterRef, expirationTime, isActiveRef]);

  return (
    <div className={styles.IdleTimeContainer}>
      {renderCounter}
    </div>
  );
};

export const IdleProvider: FC<IdleContextProviderProps> = ({ children, kiosk }) => {
  const { replace } = useHistory();
  const isActiveRef = useRef(false);
  const expirationTime = useRef(kiosk?.expirationTime ?? EXPIRATION_DEFAULT_TIME);
  const counterRef = useRef<number>(expirationTime.current);

  const handleActiveIdleTimer = useCallback(() => {
    expirationTime.current = kiosk?.expirationTime ?? NO_TIMER;
    counterRef.current = expirationTime.current;
    isActiveRef.current = true;
    console.log("[IdleTimer] activated — expiration:", expirationTime.current, "s");
  }, [kiosk]);

  const handleActiveDefaultIdleTimer = useCallback(() => {
    expirationTime.current = kiosk?.expirationTime ?? EXPIRATION_DEFAULT_TIME;
    counterRef.current = expirationTime.current;
    isActiveRef.current = true;
    console.log("[IdleTimer] activated (default) — expiration:", expirationTime.current, "s");
  }, [kiosk]);

  const handleDisableIdleTimer = useCallback(() => {
    isActiveRef.current = false;
    console.log("[IdleTimer] disabled");
  }, []);

  const resetCounter = useCallback(() => {
    counterRef.current = expirationTime.current;
  }, []);

  const handleExpire = useCallback(() => {
    isActiveRef.current = false;
    replace("/");
  }, [replace]);

  return (
    <IdleContext.Provider
      value={{
        resetCounter,
        handleActiveDefaultIdleTimer,
        handleActiveIdleTimer,
        handleDisableIdleTimer,
      }}
    >
      {children}
      <IdleCounterDisplay
        counterRef={counterRef}
        expirationTime={expirationTime}
        isActiveRef={isActiveRef}
        onExpire={handleExpire}
      />
    </IdleContext.Provider>
  );
};

export const useIdle = () => {
  const context = useContext(IdleContext);
  return context;
};
